import { ReactNode, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AuthContext, User } from './auth-context';
import { initializeWebSocket, disconnectWebSocket } from '@/shared/services/websocketService';
import { useWebSocketStore } from '@/shared/stores/websocketStore';
import { useAuthStore } from '@/shared/stores/authStore';
import { offlineQueueService } from '@/shared/services/offlineQueueService';
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';
import { useUIStore } from '@/shared/stores/uiStore';
import { useTimerStore } from '@/shared/stores/timerStore';
import { timerPersistence } from '@/shared/utils/timerPersistence';
import { setAccessToken } from './tokenStore';
import { refreshAccessToken, resetRefreshState, apiClient } from './api-client';
import { STORAGE_KEYS } from './constants';

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // accessToken lives in React state so isAuthenticated is fully reactive.
  // The module-level tokenStore variable is kept in sync for use by the
  // Axios interceptor (which runs outside of React).
  const [accessToken, setAccessTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setConnectionState } = useWebSocketStore();
  const setStoreUser = useAuthStore((state) => state.setUser);
  const { addNotification } = useUIStore();
  const { wasOffline } = useOnlineStatus();

  // Sync user to auth store for hooks that use the store directly
  useEffect(() => {
    if (user) {
      setStoreUser({
        id: user.id,
        email: user.email,
        name: user.name,
        avatarUrl: user.avatarUrl,
        emailVerified: user.emailVerified,
        role: (user.role as any) ?? 'member',
        workspaceId: user.workspaceId ?? '',
        workspaceName: '',
      } as any);
    }
  }, [user, setStoreUser]);

  const connectWebSocket = useCallback((token: string) => {
    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:3001/ws';
    const service = initializeWebSocket(wsUrl, token);

    service.on('connection:established', () => setConnectionState('connected'));
    service.on('connection:closed', () => setConnectionState('disconnected'));
    service.on('connection:error', () => setConnectionState('disconnected'));

    service.connect();
  }, [setConnectionState]);

  const connectWebSocketRef = useRef(connectWebSocket);
  useEffect(() => { connectWebSocketRef.current = connectWebSocket; }, [connectWebSocket]);

  // Sync queued offline actions when the network comes back
  useEffect(() => {
    if (!wasOffline || !accessToken) return;

    const syncOfflineData = async () => {
      const stats = await offlineQueueService.getQueueStats();
      if (stats.pending > 0 || stats.failed > 0) {
        addNotification({ type: 'info', message: `Syncing ${stats.pending + stats.failed} offline items...`, duration: 3000 });
        const result = await offlineQueueService.processQueue();
        if (result.success && result.synced > 0) {
          addNotification({ type: 'success', message: `Synced ${result.synced} items successfully`, duration: 3000 });
        }
        if (result.failed > 0) {
          addNotification({ type: 'warning', message: `${result.failed} items failed to sync. Will retry.`, duration: 5000 });
        }
      }
    };

    syncOfflineData();
  }, [wasOffline, accessToken, addNotification]);

  // ── Session restore on mount ────────────────────────────────────────────────
  // Runs once. Skipped on the OIDC callback path — OidcCallbackPage owns that
  // flow and will call login() itself once it has the user + token.
  useEffect(() => {
    const isOidcCallback =
      globalThis.location.pathname === '/auth/callback' &&
      globalThis.location.search.includes('token=');

    if (isOidcCallback) {
      // Don't attempt a silent refresh — the callback page will call login().
      // Just unblock the loading gate immediately.
      setIsLoading(false);
      return;
    }

    // Optimistically paint stale user data while the refresh is in flight
    const storedUser = localStorage.getItem(STORAGE_KEYS.AUTH_USER);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser) as User);
      } catch {
        localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
      }
    }

    const restoreSession = async () => {
      try {
        const token = await refreshAccessToken();
        // Both stores must be updated together so isAuthenticated becomes true
        // in the same render cycle.
        setAccessTokenState(token);
        setAccessToken(token);
        connectWebSocketRef.current(token);
      } catch {
        // No valid refresh cookie — clear any stale local data
        setUser(null);
        setAccessTokenState(null);
        setAccessToken(null);
        localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── logout ─────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    apiClient.post('/auth/logout', {}, { withCredentials: true }).catch(() => undefined);

    setUser(null);
    setAccessTokenState(null);
    setAccessToken(null);
    localStorage.removeItem(STORAGE_KEYS.AUTH_USER);

    useTimerStore.getState().reset();
    timerPersistence.clear();
    disconnectWebSocket();
    setConnectionState('disconnected');
  }, [setConnectionState]);

  // Listen for 401-triggered session expiry from the Axios interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      logout();
      addNotification({ type: 'warning', message: 'Your session has expired. Please log in again.', duration: 5000 });
    };
    globalThis.addEventListener('auth:sessionExpired', handleSessionExpired);
    return () => globalThis.removeEventListener('auth:sessionExpired', handleSessionExpired);
  }, [addNotification, logout]);

  // ── login ──────────────────────────────────────────────────────────────────
  const login = useCallback((newUser: User, token: string) => {
    resetRefreshState();

    // Update both the React state (makes isAuthenticated reactive) and the
    // module-level store (read by the Axios interceptor outside React).
    setUser(newUser);
    setAccessTokenState(token);
    setAccessToken(token);
    localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(newUser));

    connectWebSocket(token);
  }, [connectWebSocket]);

  // ── context value ──────────────────────────────────────────────────────────
  // isAuthenticated is derived from reactive state only — no side-effectful
  // getAccessToken() call — so it updates in the same render as login().
  const value = useMemo(() => ({
    user,
    accessToken,
    isAuthenticated: !!user && !!accessToken,
    isLoading,
    login,
    logout,
  }), [user, accessToken, isLoading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { useAuth } from './use-auth';
